/* The FAQ page's search field.
 *
 * Heidi, 2026-09-16: "add a search bar that someone can type in a question and
 * see answer options". So this does not filter the page. It reads the thirty
 * questions already in the DOM, and offers the ones that match; choosing one
 * opens that answer and takes the reader to it.
 *
 * ⚠️ THE FIELD IS HIDDEN IN THE MARKUP AND THIS SCRIPT UNHIDES IT. A search box
 * that swallows what somebody types is worse than no search box, and this page
 * works completely without JavaScript: every answer is real text inside a
 * <details>, and the browser's own find-in-page opens a closed one.
 *
 * ⚠️ IT SEARCHES THE ANSWERS TOO, AND SHOWS THE QUESTION. Somebody looking for
 * "gift card" is looking for the answer that mentions one, which may sit under
 * a question that does not. A question match still sorts above an answer match.
 *
 * ⚠️ NOTHING HERE USES innerHTML. The labels are built from text nodes, because
 * every string in them comes from the page's own content and the highlight has
 * to survive a question with an ampersand in it.
 */
(function () {
  'use strict';

  var MAX_RESULTS = 8;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Two normalisations, and the difference matters. `norm` collapses runs of
  // whitespace, which is right for matching and wrong for a highlight: it
  // changes the string's length, so an offset found in it does not line up with
  // the text the reader sees. `flat` only lowercases and squares off the smart
  // quotes, both of which are one character for one character.
  function flat(s) {
    return (s || '').toLowerCase().replace(/[‘’“”]/g, "'");
  }

  function norm(s) {
    return flat(s).replace(/\s+/g, ' ').trim();
  }

  function setup(root) {
    var box = root.querySelector('[data-mv-faq-search]');
    var input = root.querySelector('[data-mv-faq-input]');
    var list = root.querySelector('[data-mv-faq-results]');
    var live = root.querySelector('[data-mv-faq-live]');
    if (!box || !input || !list) return;

    // One entry per question, read once: the page does not change under us.
    var items = [];
    Array.prototype.forEach.call(root.querySelectorAll('.mv-faq__item'), function (el, i) {
      var qh = el.querySelector('.mv-faq__qh');
      var a = el.querySelector('.mv-faq__a');
      if (!qh) return;
      if (!el.id) el.id = 'mv-faq-q' + (i + 1);
      var band = el.closest('.mv-faq__band');
      var group = band ? band.querySelector('.mv-faq__group') : null;
      items.push({
        el: el,
        q: qh.textContent.trim(),
        group: group ? group.textContent.trim() : '',
        qn: norm(qh.textContent),
        qflat: flat(qh.textContent.trim()),
        an: norm(a ? a.textContent : '')
      });
    });
    if (!items.length) return;

    var open = false;
    var active = -1;
    var shown = [];

    box.hidden = false;

    function label(item, query) {
      var frag = document.createDocumentFragment();
      var q = document.createElement('span');
      var at = query ? item.qflat.indexOf(query) : -1;
      if (at === -1) {
        q.appendChild(document.createTextNode(item.q));
      } else {
        q.appendChild(document.createTextNode(item.q.slice(0, at)));
        var mark = document.createElement('mark');
        mark.appendChild(document.createTextNode(item.q.slice(at, at + query.length)));
        q.appendChild(mark);
        q.appendChild(document.createTextNode(item.q.slice(at + query.length)));
      }
      frag.appendChild(q);
      if (item.group) {
        var g = document.createElement('span');
        g.className = 'mv-faq__result-group';
        g.appendChild(document.createTextNode(item.group));
        frag.appendChild(g);
      }
      return frag;
    }

    function close() {
      open = false;
      active = -1;
      list.hidden = true;
      list.textContent = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }

    function mark(i) {
      active = i;
      Array.prototype.forEach.call(list.querySelectorAll('[role="option"]'), function (opt, n) {
        var on = n === i;
        opt.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) {
          input.setAttribute('aria-activedescendant', opt.id);
          if (opt.scrollIntoView) opt.scrollIntoView({ block: 'nearest' });
        }
      });
      if (i < 0) input.removeAttribute('aria-activedescendant');
    }

    function choose(i) {
      var item = shown[i];
      if (!item) return;
      close();
      item.el.open = true;
      var summary = item.el.querySelector('summary');
      if (summary) summary.focus({ preventScroll: true });
      // ⚠️ THE JUMP IS INSTANT, AND `behavior: 'smooth'` IS WHY. Measured in
      // Chrome on 2026-09-16: choosing a result opened the right answer, moved
      // the keyboard focus onto it, and left the page exactly where it was,
      // with the answer 1,068px below the fold and no error anywhere. The same
      // call with `behavior: 'auto'` scrolled to 771px. A browser is free to
      // drop a smooth scroll — a background tab is one case — and what it drops
      // is the whole scroll, not the animation.
      //
      // It reads as a jump rather than a glide, which is what a search result
      // should do. `.mv-faq__item--landed` marks the row for a moment so the
      // reader can see where they arrived.
      item.el.scrollIntoView({ block: 'center' });
      item.el.classList.add('mv-faq__item--landed');
      window.setTimeout(function () { item.el.classList.remove('mv-faq__item--landed'); }, 1600);
    }

    function render() {
      var query = norm(input.value);
      list.textContent = '';
      if (query.length < 2) { close(); if (live) live.textContent = ''; return; }

      var inQ = [];
      var inA = [];
      items.forEach(function (item) {
        if (item.qn.indexOf(query) !== -1) inQ.push(item);
        else if (item.an.indexOf(query) !== -1) inA.push(item);
      });
      shown = inQ.concat(inA).slice(0, MAX_RESULTS);
      var total = inQ.length + inA.length;

      if (!shown.length) {
        var none = document.createElement('li');
        none.className = 'mv-faq__result--empty';
        none.appendChild(document.createTextNode('No question matches that. Try a word like delivery, subscription or catering.'));
        list.appendChild(none);
      } else {
        shown.forEach(function (item, n) {
          var li = document.createElement('li');
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'mv-faq__result';
          btn.id = 'mv-faq-result-' + n;
          btn.setAttribute('role', 'option');
          btn.setAttribute('aria-selected', 'false');
          btn.appendChild(label(item, query));
          btn.addEventListener('mousedown', function (e) { e.preventDefault(); });
          btn.addEventListener('click', function () { choose(n); });
          li.appendChild(btn);
          list.appendChild(li);
        });
      }

      open = true;
      active = -1;
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      input.removeAttribute('aria-activedescendant');
      if (live) {
        live.textContent = total
          ? total + (total === 1 ? ' question matches.' : ' questions match.')
          : 'No questions match.';
      }
    }

    input.addEventListener('input', render);

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        if (open) close();
        else input.value = '';
        return;
      }
      if (e.key === 'Enter') {
        if (open && shown.length) {
          e.preventDefault();
          choose(active < 0 ? 0 : active);
        }
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (!open) { render(); return; }
      if (!shown.length) return;
      e.preventDefault();
      var next = e.key === 'ArrowDown' ? active + 1 : active - 1;
      if (next >= shown.length) next = 0;
      if (next < 0) next = shown.length - 1;
      mark(next);
    });

    // A click anywhere else puts the list away. `mousedown` on a result is
    // cancelled above, so choosing one never races this.
    document.addEventListener('click', function (e) {
      if (open && !box.contains(e.target)) close();
    });
  }

  // The section links (Ryan's FAQ brief, 2026-09-28, Task 2g). The links work
  // without this. It only marks the pill for the section on screen with
  // aria-current, and keeps that pill in view when the row scrolls sideways.
  //
  // The current section is the last heading above LINE, which sits just under
  // the stuck bar (69px). It is read on scroll, once per frame.
  //
  // ⚠️ NOT AN IntersectionObserver, although the brief allows one. The first
  // version used one, and the pill lagged a section behind after every jump:
  // a smooth scroll crosses the line mid-flight, the observer fires then, and
  // nothing fires again when the heading comes to rest. Measured 2026-09-28.
  function setupNav(root) {
    var nav = root.querySelector('[data-mv-faq-nav]');
    if (!nav) return;
    var row = nav.querySelector('ul');
    var links = [];
    Array.prototype.forEach.call(nav.querySelectorAll('a[href^="#"]'), function (a) {
      var target = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
      if (target) links.push({ a: a, target: target });
    });
    if (!links.length) return;

    var LINE = 96;
    var current = null;
    function update() {
      var next = null;
      links.forEach(function (l) {
        if (l.target.getBoundingClientRect().top <= LINE) next = l;
      });
      if (next === current) return;
      if (current) current.a.removeAttribute('aria-current');
      current = next;
      if (!current) return;
      current.a.setAttribute('aria-current', 'true');
      var left = current.a.offsetLeft - (row.clientWidth - current.a.offsetWidth) / 2;
      row.scrollTo({ left: Math.max(0, left), behavior: 'auto' });
    }

    var queued = false;
    window.addEventListener('scroll', function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () { queued = false; update(); });
    }, { passive: true });
    update();
  }

  ready(function () {
    Array.prototype.forEach.call(document.querySelectorAll('[data-mv-faq]'), function (root) {
      setup(root);
      setupNav(root);
    });
  });
})();
